import type {ItemObject, ItemType} from '@PosterWhiteboard/items/item/item.types';
import {openMessageModal} from '@Modals/message-modal';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {showMessageGrowl} from '@Components/message-growl/message-growl';
import {getUserId, isUserDesigner, isUserLoggedIn} from '@Libraries/user.library';
import {openErrorModal} from '@Modals/error-modal';
import {ITEM_CONTROL_DIMENSIONS} from '@PosterWhiteboard/poster/poster-item-controls';
import {GROWL_TYPE} from '@Components/message-growl';
import type React from 'react';
import {createItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import type {FabricPositionalParams} from '@Utils/fabric.util';
import {copyToClipboard, readFromClipboard} from '@Utils/clipboard.util';
import {ElementDataType, isTempImageData} from '@Libraries/add-media-library';
import {isJson} from '@Utils/string.util';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {ItemData} from './add-item.class';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import {Point} from '@postermywall/fabricjs-2';
import {DEFAULT_TEXT_FONT_FAMILY} from '@PosterWhiteboard/classes/text-styles.class';
import {getRecommendedFont, getScriptForText} from '@Libraries/font-library';
import {
  getMediaStorageForbackendData,
  type UploadFileDataTypes,
  uploadFiles,
  type UserMediaType,
  type UserMediaVOResponse,
  userMediaVOToElementData,
} from '@Libraries/user-media-library';
import {isArray, isObject} from 'lodash';
import {onAddItems} from '@Components/poster-editor/library/poster-editor-open-modals';
import {updateUnlinkedMediaState} from '@Panels/user-media-panel/user-media-panel-reducer';
import {POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';

interface ClipboardPasteInfo {
  isPasteFromRightClick: boolean;
  eventCanvasOffset?: FabricPositionalParams;
}

interface PosterClipboardData {
  key: string;
  version: string;
  graphicItemObjects: ItemObject[];
  audioItemObject?: AudioItemObject;
  posterInfo: ClipboardPosterInfo;
  group: Record<string, number>;
}

interface ClipboardPosterInfo {
  width: number;
  height: number;
  idGalleryTemplate?: string;
  idGalleryTemplateCreator: number;
}

/**
 * Makes sure user isn't able to copy-paste items from an older version poster editor where item structure was different
 */
const PASTE_ITEMS_VERSION = `${POSTER_VERSION.CURRENT}_1`;

export class PageClipboard extends CommonMethods {
  public page: Page;

  public constructor(page: Page) {
    super();
    this.page = page;
  }

  public copySelection(e?: Event | React.MouseEvent<HTMLElement>): void {
    const selection = this.page.getSelectedItems();

    if (selection.length) {
      let graphicItems: ItemType[] = [];

      for (let i = 0; i < selection.length; i++) {
        const selectionItem = selection[i];
        graphicItems.push(selectionItem);
      }
      if (graphicItems.length) {
        if (e) {
          e.preventDefault(); // default behaviour is to copy any selected text
        }
        graphicItems = this.page.activeSelection.orderGraphicItems(graphicItems);

        const graphicItemObjects = graphicItems.map((item) => {
          return item.toObject();
        });

        const {poster} = this.page;
        const group = this.page.activeSelection.getActiveObject();
        // if group is null this means only one graphic item is selected thus give the left and top coordinates of group same as x and y coordinates of graphic item
        const graphicItemsAndPoster: PosterClipboardData = {
          key: poster.copyKey,
          graphicItemObjects,
          version: PASTE_ITEMS_VERSION,
          posterInfo: {
            width: poster.width,
            height: poster.height,
            idGalleryTemplate: poster.idGalleryTemplate,
            idGalleryTemplateCreator: poster.idGalleryTemplateCreator,
          },
          group: {
            left: group ? group.left : graphicItemObjects[0].left,
            top: group ? group.top : graphicItemObjects[0].top,
          },
        };
        const jsonString = JSON.stringify(graphicItemsAndPoster);
        void copyToClipboard(jsonString);
      } else {
        openMessageModal({
          title: window.i18next.t('pmwjs_copying_error_title'),
          text: window.i18next.t('pmwjs_title_copy_disabled'),
        });
      }
    } else {
      void this.checkAndCopyAudioSelection();
    }
  }

  public async checkAndCopyAudioSelection(): Promise<void> {
    const audioItem = this.page.poster.audioClips.getSelectedAudioItem();

    if (!audioItem) {
      return;
    }

    const {poster} = this.page;
    const jsonString = JSON.stringify({
      key: poster.copyKey,
      version: PASTE_ITEMS_VERSION,
      graphicItemObjects: [],
      audioItemObject: audioItem.toObject(),
      posterInfo: {
        width: poster.width,
        height: poster.height,
        idGalleryTemplate: poster.idGalleryTemplate,
        idGalleryTemplateCreator: poster.idGalleryTemplateCreator,
      },
      group: {},
    } as PosterClipboardData);
    await copyToClipboard(jsonString);
  }

  public async pasteItems(e?: Event | ClipboardEvent | React.MouseEvent<HTMLElement>, clipboardPasteInfo: ClipboardPasteInfo = {isPasteFromRightClick: false}): Promise<void> {
    if (e instanceof ClipboardEvent && e.clipboardData?.files) {
      const filesArray = Array.from(e.clipboardData.files);
      const file = filesArray.find((item: File) => item.type.includes('image'));
      if (file) {
        await this.pasteExternalImageItemIfInClipboard(file);
        return;
      }
    }

    const {poster} = this.page;
    const graphicItemsAndPoster = await getItemsAndPosterDataFromClipboard();

    if (graphicItemsAndPoster) {
      if (this.isPasteAllowed(graphicItemsAndPoster)) {
        if (e) {
          e.preventDefault(); // We are already handling the data from the clipboard, we do not want it inserted into the document
        }
        showLoading('pastingGraphicItems');
        // if target poster has different dimensions than source poster paste the copied graphic items at (40,40) coordinate
        let deltaX = 15;
        let deltaY = 15;
        const graphicItems: ItemType[] = [];
        let multipleGraphicItems = graphicItemsAndPoster.graphicItemObjects.length > 1;

        if (!this.isMultipleItemsPasteAllowed(graphicItemsAndPoster) && multipleGraphicItems) {
          graphicItemsAndPoster.graphicItemObjects = graphicItemsAndPoster.graphicItemObjects.slice(0, 1);
          multipleGraphicItems = false;
          showMessageGrowl({text: window.i18next.t('pmwjs_only_one_item_paste_limit'), type: GROWL_TYPE.PREMIUM});
        }

        if (poster.width !== graphicItemsAndPoster.posterInfo.width || poster.height !== graphicItemsAndPoster.posterInfo.height) {
          deltaX = 40 - graphicItemsAndPoster.group.left;
          deltaY = 40 - graphicItemsAndPoster.group.top;
        }

        const graphicItemPromises = [];

        for (const item of graphicItemsAndPoster.graphicItemObjects) {
          graphicItemPromises.push(
            new Promise<void>((resolve, reject) => {
              createItemFromObject(this.page, item)
                .then((graphicItem) => {
                  const templateCreator = graphicItemsAndPoster.posterInfo.idGalleryTemplateCreator;
                  if (!this.isItemPasteAllowed(graphicItemsAndPoster.key, graphicItem.idOriginalOwner, templateCreator)) {
                    hideLoading('pastingGraphicItems');
                    openErrorModal({
                      title: window.i18next.t('pmwjs_pasting_error_title'),
                      message: window.i18next.t('pmwjs_item_pasting_error'),
                    });
                    reject(new Error('Item not alloed to be pasted'));
                    return;
                  }
                  if (graphicItemsAndPoster.posterInfo.idGalleryTemplate) {
                    graphicItem.idOriginalOwner = templateCreator;
                  }

                  // Only set coordinates of new graphic item if it's added through right click/touch event, and not keypress shortcut.
                  const eventOffsetX = clipboardPasteInfo.eventCanvasOffset?.left;
                  const eventOffsetY = clipboardPasteInfo.eventCanvasOffset?.top;

                  if (clipboardPasteInfo.isPasteFromRightClick && eventOffsetX !== undefined && eventOffsetY !== undefined) {
                    // not setting equal to popMenuOffset values just in case a group of items is being pasted in which case
                    // the subtraction in equations below is not equal to zero.
                    graphicItem.fabricObject.setXY(
                      new Point(
                        eventOffsetX + (graphicItem.fabricObject.getX() - graphicItemsAndPoster.group.left),
                        eventOffsetY + (graphicItem.fabricObject.getY() - graphicItemsAndPoster.group.top)
                      )
                    );
                  }
                  graphicItem.onPageStartTime = 0;
                  graphicItem.onPageEndTime = this.page.getDuration();

                  graphicItems.push(graphicItem);

                  resolve();
                })
                .catch(reject);
            })
          );
        }

        const responses = await Promise.allSettled(graphicItemPromises);

        for (const response of responses) {
          if (response.status === 'rejected') {
            console.error('Item failed to paste details:', response);
          }
        }

        if (clipboardPasteInfo.isPasteFromRightClick) {
          deltaX = multipleGraphicItems ? ITEM_CONTROL_DIMENSIONS.PMW_CONTROL_PADDING : graphicItems[0].getSelectorPadding();
          deltaY = deltaX;
        }

        this.page.activeSelection.duplicateSelection(graphicItems, deltaX, deltaY);
        hideLoading('pastingGraphicItems');
      } else {
        openMessageModal({
          title: window.i18next.t('pmwjs_pasting_error_title'),
          text: window.i18next.t('pmwjs_pasting_error'),
        });
      }
    }

    await this.pasteAudioItemIfInClipboard(e);
    await this.pasteExternalTextItemIfInClipboard(e);
  }

  public async pasteExternalImageItemIfInClipboard(image: File | undefined): Promise<void> {
    if (!image) {
      return;
    }
    await uploadFiles({onFileUpload: onPastedImageUpload, onUploadError: onPastedImageUploadError, onAfterUpload: onAfterPastedImageUpload}, [image], {
      shouldLoadDataUrlImage: true,
    });
  }

  public async pasteExternalTextItemIfInClipboard(e?: Event | React.MouseEvent<HTMLElement>): Promise<void> {
    const text = await getExternalTextItemDataFromClipboard();
    if (!text) {
      return;
    }

    if (e) {
      e.preventDefault();
    }

    const script = getScriptForText(text);
    const textItemData = {
      type: ElementDataType.TEXT,
      text: text,
    } as ItemData;
    void this.page.items.addItems.addTextItem(textItemData, {
      textStyles: {
        script: script,
        fontFamily: script ? getRecommendedFont(script) : DEFAULT_TEXT_FONT_FAMILY,
      },
    });
  }

  public async pasteAudioItemIfInClipboard(e?: Event | React.MouseEvent<HTMLElement>): Promise<void> {
    const getAudioItemsDataIfExists = await getAudioItemDataFromClipboard();
    if (!getAudioItemsDataIfExists) {
      return;
    }

    if (!this.isPasteAllowed(getAudioItemsDataIfExists, true)) {
      openMessageModal({
        title: window.i18next.t('pmwjs_pasting_error_title'),
        text: window.i18next.t('pmwjs_pasting_error'),
      });
    }

    if (e) {
      e.preventDefault();
    }

    try {
      if (getAudioItemsDataIfExists.audioItemObject) {
        await this.page.items.addItems.addAudioItemFromAudioItemObject(getAudioItemsDataIfExists.audioItemObject);
      }
    } catch (err) {
      console.log(err);
      openMessageModal({
        title: window.i18next.t('pmwjs_pasting_error_title'),
        text: window.i18next.t('pmwjs_pasting_error'),
      });
    }
  }

  public isPasteAllowed(graphicItemsAndPoster: PosterClipboardData, isForAudioItem = false): boolean {
    if (isForAudioItem) {
      return window.PMW.currentUserOnPageLoad?.isSuper || graphicItemsAndPoster.key === this.page.poster.copyKey || !getUserId() || !isUserDesigner();
    }

    return (
      window.PMW.currentUserOnPageLoad?.isSuper ||
      graphicItemsAndPoster.key === this.page.poster.copyKey ||
      !graphicItemsAndPoster.posterInfo.idGalleryTemplate ||
      graphicItemsAndPoster.posterInfo.idGalleryTemplateCreator === getUserId() ||
      !getUserId() ||
      !isUserDesigner()
    );
  }

  public isItemPasteAllowed(copyKey: string, idOriginalOwner?: number, templateCreator?: number): boolean {
    return (
      window.PMW.currentUserOnPageLoad?.isSuper ||
      !getUserId() ||
      !isUserDesigner() ||
      copyKey === this.page.poster.copyKey ||
      idOriginalOwner === getUserId() ||
      templateCreator === getUserId() ||
      !(templateCreator || idOriginalOwner)
    );
  }

  /**
   * Checks if user is allowed to paste multiple items at a time
   */
  public isMultipleItemsPasteAllowed(graphicItemsAndPoster: PosterClipboardData): boolean {
    return (
      window.PMW.currentUserOnPageLoad?.isSuper ||
      graphicItemsAndPoster.key === this.page.poster.copyKey ||
      !graphicItemsAndPoster.posterInfo.idGalleryTemplate ||
      graphicItemsAndPoster.posterInfo.idGalleryTemplateCreator === getUserId()
    );
  }
}

const getDataFromClipboard = async (): Promise<unknown> => {
  let itemsAndPoster;
  try {
    const clipText = await readFromClipboard();
    itemsAndPoster = JSON.parse(clipText) as unknown;
  } catch (err) {
    return undefined;
  }

  return itemsAndPoster;
};

const onPastedImageUpload = (type: UserMediaType, data: UploadFileDataTypes): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  if (!isArray(data)) {
    if (isTempImageData(data)) {
      onAddItems([data]);
    } else {
      if (!isUserLoggedIn()) {
        const mediaItem = getMediaStorageForbackendData(data, type);
        window.PMW.redux.store.dispatch(updateUnlinkedMediaState([mediaItem]));
      }

      const itemData = userMediaVOToElementData(type, data);
      onAddItems([itemData]);
    }
  }
};

const onAfterPastedImageUpload = (type: UserMediaType, data: UserMediaVOResponse[] | UserMediaVOResponse): void => {
  if (!isUserLoggedIn() && !isArray(data)) {
    const mediaItem = getMediaStorageForbackendData(data, type);
    window.PMW.redux.store.dispatch(updateUnlinkedMediaState([mediaItem]));
  }
};

const onPastedImageUploadError = (): void => {
  openErrorModal.bind({message: window.i18next.t('pmwjs_cannot_upload_error_msg')});
};

const getExternalTextDataFromClipboard = async (): Promise<string | undefined> => {
  let clipText: string;
  try {
    clipText = await readFromClipboard();
    if (isJson(clipText)) {
      return undefined;
    }
  } catch (err) {
    return undefined;
  }

  return clipText;
};

const getItemsAndPosterDataFromClipboard = async (): Promise<PosterClipboardData | undefined> => {
  let itemsAndPoster;

  try {
    const clipText = await readFromClipboard();
    itemsAndPoster = JSON.parse(clipText) as unknown;
  } catch (err) {
    return undefined;
  }

  const validateditemsAndPoster = getValidatedItemsAndPosterClipboardData(itemsAndPoster);
  if (validateditemsAndPoster) {
    return validateditemsAndPoster;
  }

  return undefined;
};

const getAudioItemDataFromClipboard = async (): Promise<PosterClipboardData | undefined> => {
  const itemsAndPoster = await getDataFromClipboard();

  if (!itemsAndPoster) {
    return undefined;
  }

  const validateAudioItemData = getItemsAndPosterClipboardDataForAudio(itemsAndPoster);
  if (validateAudioItemData) {
    return validateAudioItemData;
  }

  return undefined;
};

const getExternalTextItemDataFromClipboard = async (): Promise<string | undefined> => {
  const text = await getExternalTextDataFromClipboard();

  if (!text || (text && !text.trim())) {
    return undefined;
  }

  return text.trim();
};

export const getItemsAndPosterClipboardDataForAudio = (data: unknown): PosterClipboardData | undefined => {
  if (isObject(data) && 'key' in data && 'version' in data && data.version !== PASTE_ITEMS_VERSION && 'audioItemObject' in data) {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_audio_clip_pasting_error'),
    });
    return undefined;
  }

  if (isObject(data) && 'key' in data && 'audioItemObject' in data && 'posterInfo' in data) {
    return data as PosterClipboardData;
  }
  return undefined;
};

export const getValidatedItemsAndPosterClipboardData = (data: unknown): PosterClipboardData | undefined => {
  if (isObject(data) && 'key' in data && 'version' in data && data.version !== PASTE_ITEMS_VERSION && 'graphicItemObjects' in data) {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_items_pasting_error'),
    });
    return undefined;
  }

  if (isObject(data) && 'key' in data && 'graphicItemObjects' in data && 'posterInfo' in data && 'group' in data) {
    return data as PosterClipboardData;
  }
  return undefined;
};
